import type { SalesResponse } from "@/lib/dashApi";
import { fmtCurrency, fmtNumber } from "@/lib/format";
import { KpiCard, Panel, SourceBadge, StatusPill } from "@/components/dash/ui";

function Warnings({ items }: { items: string[] }) {
  if (!items.length) return null;
  return (
    <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 px-4 py-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-amber-300">Warnings</p>
      <ul className="mt-1.5 space-y-1 text-xs text-amber-200/80">
        {items.map((w, i) => (
          <li key={i} className="flex gap-2"><span className="text-amber-500">•</span>{w}</li>
        ))}
      </ul>
    </div>
  );
}

/** The combined-sales accuracy view: totals (incl/excl/tax), by-source,
 *  by-Odoo-company, and repairs shown separately (never added to the total). */
export default function SalesOverview({ sales }: { sales: SalesResponse }) {
  const t = sales.totals;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <KpiCard label="Total sales (incl. tax)" value={t.business_incl} unit="EUR" />
        <KpiCard label="Total sales (excl. tax)" value={t.business_excl} unit="EUR" />
        <KpiCard label="Total tax" value={t.business_tax} unit="EUR" />
        <KpiCard label="Unpaid (Odoo)" value={t.unpaid} unit="EUR" tone="danger" />
      </div>
      <p className="-mt-3 text-xs text-slate-500">Basis: {t.basis}</p>

      <Warnings items={sales.warnings} />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Panel title="Sales by source" subtitle="Authoritative — feeds the combined total">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
                <th className="pb-2 font-medium">Source</th>
                <th className="pb-2 text-right font-medium">Incl. tax</th>
                <th className="pb-2 text-right font-medium">Excl. tax</th>
                <th className="pb-2 text-right font-medium">Tax</th>
                <th className="pb-2 text-right font-medium">Count</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-500/40">
              {sales.sources.map((s) => (
                <tr key={s.key} className="text-slate-300">
                  <td className="py-2"><span className="flex items-center gap-2"><SourceBadge code={s.key} /><StatusPill status={s.status} /></span></td>
                  <td className="py-2 text-right tabular-nums text-white">{fmtCurrency(s.incl)}</td>
                  <td className="py-2 text-right tabular-nums">{fmtCurrency(s.excl)}</td>
                  <td className="py-2 text-right tabular-nums text-slate-400">{fmtCurrency(s.tax)}</td>
                  <td className="py-2 text-right tabular-nums">{fmtNumber(s.count)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>

        <Panel title="Odoo — by company" subtitle="Posted customer invoices, net of refunds">
          {sales.odoo_companies.length === 0 ? (
            <p className="py-4 text-center text-sm text-slate-500">No company invoices in range.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
                  <th className="pb-2 font-medium">Company</th>
                  <th className="pb-2 text-right font-medium">Incl.</th>
                  <th className="pb-2 text-right font-medium">Excl.</th>
                  <th className="pb-2 text-right font-medium">Tax</th>
                  <th className="pb-2 text-right font-medium">Inv.</th>
                  <th className="pb-2 text-right font-medium">Unpaid</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-500/40">
                {sales.odoo_companies.map((c) => (
                  <tr key={c.company_id} className="text-slate-300">
                    <td className="py-2">{c.name}</td>
                    <td className="py-2 text-right tabular-nums text-white">{fmtCurrency(c.sales_incl)}</td>
                    <td className="py-2 text-right tabular-nums">{fmtCurrency(c.sales_excl)}</td>
                    <td className="py-2 text-right tabular-nums text-slate-400">{fmtCurrency(c.tax)}</td>
                    <td className="py-2 text-right tabular-nums">{c.invoices}</td>
                    <td className="py-2 text-right tabular-nums text-amber-300/90">{fmtCurrency(c.unpaid)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>
      </div>

      {sales.repairs && (
        <Panel
          title={<span className="flex items-center gap-2"><SourceBadge code="repairs" /> Repair revenue (shown separately)</span>}
          subtitle="Estimate from approved quotes — already invoiced into Odoo, so NOT added to the total"
        >
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <KpiCard label="Repair sales (incl.)" value={sales.repairs.incl} unit="EUR" source="repairs" />
            <KpiCard label="Repair sales (excl.)" value={sales.repairs.excl} unit="EUR" />
            <KpiCard label="Repair tax" value={sales.repairs.tax} unit="EUR" />
            <KpiCard label="Approved jobs" value={sales.repairs.jobs} unit="count" />
          </div>
          {sales.repairs.branch_split.length > 0 && (
            <table className="mt-4 w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
                  <th className="pb-2 font-medium">Branch</th>
                  <th className="pb-2 text-right font-medium">Jobs</th>
                  <th className="pb-2 text-right font-medium">Incl.</th>
                  <th className="pb-2 text-right font-medium">Excl.</th>
                  <th className="pb-2 text-right font-medium">Tax</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-500/40">
                {sales.repairs.branch_split.map((b, i) => (
                  <tr key={i} className="text-slate-300">
                    <td className="py-2">{b.name}</td>
                    <td className="py-2 text-right tabular-nums">{b.jobs}</td>
                    <td className="py-2 text-right tabular-nums text-white">{fmtCurrency(b.incl)}</td>
                    <td className="py-2 text-right tabular-nums">{fmtCurrency(b.excl)}</td>
                    <td className="py-2 text-right tabular-nums text-slate-400">{fmtCurrency(b.tax)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p className="mt-3 rounded-md border border-ink-500/50 bg-ink-700/40 px-3 py-2 text-xs text-slate-400">
            ⚠ {sales.repairs.note}
          </p>
        </Panel>
      )}
    </div>
  );
}
